import { useCallback, useEffect, useMemo, useState } from 'react'

import { encode, decode } from 'js-base64'

import { startCase } from '@bettermode/common/startcase'
import {
  CustomFieldType,
  CustomFieldValidators,
  MassActionRequest,
  MassActionRequestAction,
  MassActionRequestContext,
  MemberInvitation,
  MemberInvitationStatus,
  Role,
  RoleType,
} from '@tribeplatform/gql-client/types'
import { CustomFieldSubtype } from '@tribeplatform/react-components/CustomField'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  getTranslatedRoleName,
  UserImportModal,
} from '@tribeplatform/react-components/UserImport'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useMemberInvitations,
  useNetwork,
  useNetworkRoles,
  useResendMemberInvitation,
  useRevokeMemberInvitation,
  useUpdateMemberInvitation,
} from '@tribeplatform/react-sdk/hooks'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useQuery } from '../../../hooks/useQuery.js'
import { getTranslatedInvitationStatus } from '../../../utils/members.utils.js'
import { useDebounce } from '../../../utils/useDebounce.js'
import { Table } from '../../Table/index.js'
import { EmailStatusRenderer } from '../EmailLogs/EmailStatusRenderer.js'
import { MassActionBanner } from '../MassActions/MassActionBanner.js'
import { MassActionModal } from '../MassActions/MassActionModal.js'

export const Invitations = () => {
  let initialState = {
    query: null,
    filterBy: [],
    orderBy: null,
  }
  const intl = useI18n()
  const { $t } = intl
  const { replace } = useRouter()
  const { state: queryState } = useQuery()
  if (queryState) {
    try {
      initialState = { ...initialState, ...JSON.parse(decode(queryState)) }
    } catch (error) {
      logger.error('Could not parse initial state for members')
    }
  }

  const [state, setState] = useState(initialState)
  const debouncedQuery = useDebounce(state?.query, 300)
  const [inviteMembersModal, setInviteMembersModal] = useState(false)
  const [massActionModal, setMassActionModal] =
    useState<MassActionRequestAction>(null)
  const [massActionRequest, setMassActionRequest] =
    useState<MassActionRequest>(null)
  const {
    data: { roles },
  } = useNetwork()

  useEffect(() => {
    replace(`?state=${encode(JSON.stringify(state))}`)
  }, [state])

  let newFilterBy = [...state.filterBy]
  newFilterBy = newFilterBy?.map(filter => {
    if (filter?.key === 'inviter') {
      return {
        ...filter,
        key: 'inviterId',
      }
    }
    if (filter?.key === 'role.name') {
      const roleName = JSON.parse(filter?.value)
      const roleId = roles
        ?.filter(role => role.name === roleName)
        ?.map(role => role.id)?.[0]
      return {
        ...filter,
        key: 'roleId',
        value: JSON.stringify(roleId),
      }
    }
    if (filter?.key === 'status') {
      const status = JSON.parse(filter?.value)
      return {
        ...filter,
        value:
          // This is temporary solution until enums are fixed on the back-end
          JSON.stringify(
            status === startCase(MemberInvitationStatus.accepted)
              ? status.toLowerCase()
              : status.toUpperCase(),
          ),
      }
    }
    return filter
  })

  const {
    data,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useMemberInvitations({
    variables: {
      limit: 15,
      query: debouncedQuery,
      reverse: true,
      filterBy: newFilterBy ?? [],
    },
    useInfiniteQueryOptions: { keepPreviousData: true },
  })

  const { data: totalInvitationsData } = useMemberInvitations({
    variables: {
      limit: 0,
    },
  })

  const totalInvitations = totalInvitationsData?.pages?.[0]?.totalCount

  const { mutateAsync: resendMemberInvitation } = useResendMemberInvitation()
  const { mutateAsync: revokeMemberInvitation } = useRevokeMemberInvitation()

  const onResendInvitation = useCallback(
    async (memberInvitation: MemberInvitation) => {
      try {
        await resendMemberInvitation({
          id: memberInvitation?.id,
        })
        toast({
          title: $t({
            defaultMessage: 'Invitation Resent',
            id: 'Admin.Members.Invitation.Resent',
          }),
          description: $t({
            defaultMessage: 'Invitation is successfully resent.',
            id: 'Admin.Members.Invitation.Resent.Description',
          }),
          status: 'success',
        })
      } catch (err) {
        toast({
          title: $t({
            defaultMessage: 'Something went wrong',
            id: 'Generics.SomethingWentWrong',
          }),
          description: $t({
            defaultMessage: 'Cannot resend this invitation.',
            id: 'Admin.Members.Invitation.FailedToResend',
          }),
          status: 'error',
        })
      }
    },
    [$t, resendMemberInvitation],
  )

  const onRevokeInvitation = useCallback(
    async (memberInvitation: MemberInvitation) => {
      try {
        await revokeMemberInvitation({
          id: memberInvitation?.id,
        })
        toast({
          title: $t({
            defaultMessage: 'Invitation Revoked',
            id: 'Admin.Members.Invitation.Revoked',
          }),
          description: $t({
            defaultMessage: 'Invitation is successfully revoked.',
            id: 'Admin.Members.Invitation.Revoked.Description',
          }),
          status: 'success',
        })
      } catch (err) {
        toast({
          title: $t({
            defaultMessage: 'Something went wrong',
            id: 'Generics.SomethingWentWrong',
          }),
          description: $t({
            defaultMessage: 'Cannot revoke this invitation.',
            id: 'Admin.Members.Invitation.FailedToRevoke',
          }),
          status: 'error',
        })
      }
    },
    [$t, revokeMemberInvitation],
  )

  const networkRoles = useNetworkRoles()

  const {
    mutateAsync: updateMemberInvitation,
    isLoading: isUpdatingInvitation,
  } = useUpdateMemberInvitation()

  const changeRole = useCallback(
    async (invitation: MemberInvitation, role: Role) => {
      try {
        await updateMemberInvitation({
          id: invitation.id,
          input: {
            roleId: role.id,
          },
        })
        toast({
          title: $t({
            defaultMessage: 'Member role changed',
            id: 'Admin.Members.RoleChanged',
          }),
          description: $t(
            {
              defaultMessage:
                '{member_name}’s role is successfully changed to {role_name}.',
              id: 'Admin.Members.RoleChanged.Description',
            },
            {
              member_name: invitation.inviteeName || invitation.inviteeEmail,
              role_name: getTranslatedRoleName(intl, role.name),
            },
          ),
          status: 'success',
        })
      } catch (err) {
        toast({
          title: $t({
            defaultMessage: 'Something went wrong',
            id: 'Generics.SomethingWentWrong',
          }),
          description: $t(
            {
              defaultMessage: 'Cannot change the role of {member_name}.',
              id: 'Admin.Members.FailedToChangeRole',
            },
            {
              member_name: invitation.inviteeName || invitation.inviteeEmail,
            },
          ),
          status: 'error',
        })
      }
    },
    [$t, intl, updateMemberInvitation],
  )

  const columns = useMemo(
    () => [
      {
        Header: $t({ defaultMessage: 'ID', id: 'Generics.ID' }),
        accessor: 'id',
        renderer: 'text',
        visible: false,
      },
      {
        Header: $t({ defaultMessage: 'Email', id: 'Generics.Email' }),
        accessor: 'inviteeEmail',
        renderer: 'email',
        width: 'lg',
        visible: true,
        type: CustomFieldType.text,
        subtype: CustomFieldSubtype.EMAIL,
      },
      {
        Header: $t({ defaultMessage: 'Name', id: 'Generics.Name.Noun' }),
        accessor: 'inviteeName',
        renderer: 'text',
        visible: true,
        width: 'md',
        type: CustomFieldType.text,
      },
      {
        Header: $t({ defaultMessage: 'Created at', id: 'Generics.CreatedAt' }),
        accessor: 'createdAt',
        renderer: 'date',
        width: 'sm',
        visible: true,
        type: CustomFieldType.date,
      },
      {
        Header: $t({ defaultMessage: 'Expires at', id: 'Generics.ExpiresAt' }),
        accessor: 'expiresAt',
        renderer: 'date',
        width: 'sm',
        visible: true,
        type: CustomFieldType.date,
      },
      {
        Header: $t({ defaultMessage: 'Invited by', id: 'Generics.InvitedBy' }),
        accessor: 'inviter',
        renderer: 'member',
        width: 'md',
        visible: true,
        type: CustomFieldType.relation,
        subtype: CustomFieldSubtype.MEMBER,
        exact: true,
      },
      {
        Header: $t({ defaultMessage: 'Role', id: 'Generics.Role' }),
        accessor: 'role.name',
        renderer: (_: unknown, memberInvitation: MemberInvitation) => {
          return getTranslatedRoleName(intl, memberInvitation.role.name)
        },
        width: 'sm',
        visible: false,
        type: CustomFieldType.array,
        subtype: CustomFieldSubtype.SELECT,
        exact: true,
        items: {
          validators: [
            {
              validation: CustomFieldValidators.enum,
              value: JSON.stringify(
                roles
                  ?.filter(role => role.type !== RoleType.guest)
                  .map(role => role.name),
              ),
            },
          ],
        },
        labels: roles?.reduce((translatedLabels, role) => {
          return {
            ...translatedLabels,
            [role.name]: getTranslatedRoleName(intl, role.name),
          }
        }, {}),
      },
      {
        Header: $t({ defaultMessage: 'Status', id: 'Generics.Status' }),
        accessor: 'status',
        width: 'sm',
        visible: true,
        type: CustomFieldType.array,
        subtype: CustomFieldSubtype.SELECT,
        exact: true,
        items: {
          validators: [
            {
              validation: CustomFieldValidators.enum,
              value: JSON.stringify([
                MemberInvitationStatus.Pending,
                MemberInvitationStatus.Expired,
                MemberInvitationStatus.Revoked,
                startCase(MemberInvitationStatus.accepted),
              ]),
            },
          ],
        },
        labels: {
          [MemberInvitationStatus.Pending]: getTranslatedInvitationStatus(
            intl,
            MemberInvitationStatus.Pending,
          ),
          [MemberInvitationStatus.Expired]: getTranslatedInvitationStatus(
            intl,
            MemberInvitationStatus.Expired,
          ),
          [MemberInvitationStatus.Revoked]: getTranslatedInvitationStatus(
            intl,
            MemberInvitationStatus.Revoked,
          ),
          [startCase(MemberInvitationStatus.accepted)]:
            getTranslatedInvitationStatus(
              intl,
              MemberInvitationStatus.accepted,
            ),
        },
        renderer: (_: unknown, memberInvitation: MemberInvitation) =>
          getTranslatedInvitationStatus(intl, memberInvitation.status),
      },
      {
        Header: $t({
          defaultMessage: 'Email status',
          id: 'Generics.EmailStatus',
        }),
        accessor: 'emailLog',
        width: 'sm',
        visible: true,
        renderer: (_: unknown, memberInvitation: MemberInvitation) => (
          <EmailStatusRenderer emailLog={memberInvitation.emailLog} />
        ),
      },
      {
        visible: true,
        accessor: 'actions',
        locked: true,
        width: 'collapse',
        renderer: (_: unknown, memberInvitation: MemberInvitation) => {
          const {
            status,
            role: { name: roleName },
          } = memberInvitation

          const isAdmin = roleName === networkRoles?.admin.name
          const isModerator = roleName === networkRoles?.moderator.name
          const isStaff = isAdmin || isModerator

          return (
            <Dropdown>
              <Dropdown.ButtonMinimal as="div">
                <IconButton
                  variant="tertiaryNeutral"
                  rounded
                  icon={<SvgIcon name="dots-horizontal" />}
                  size="sm"
                />
              </Dropdown.ButtonMinimal>
              <Dropdown.Items>
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="mail" />}
                  onClick={() => onResendInvitation(memberInvitation)}
                  disabled={
                    status !== MemberInvitationStatus.Pending &&
                    status !== MemberInvitationStatus.Expired
                  }
                >
                  {$t({
                    defaultMessage: 'Resend invite',
                    id: 'Admin.Members.Invitation.Resend',
                  })}
                </Dropdown.Item>
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="trash" />}
                  onClick={() => onRevokeInvitation(memberInvitation)}
                  disabled={
                    status !== MemberInvitationStatus.Pending &&
                    status !== MemberInvitationStatus.Expired
                  }
                >
                  {$t({
                    defaultMessage: 'Revoke invite',
                    id: 'Admin.Members.Invitation.Revoke',
                  })}
                </Dropdown.Item>
                {!isAdmin && (
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="edit" />}
                    disabled={isUpdatingInvitation}
                    onClick={() =>
                      changeRole(memberInvitation, networkRoles.admin)
                    }
                  >
                    <T
                      defaultMessage="Change role to {role_name}"
                      id="Admin.Members.ChangeRoleTo"
                      values={{
                        role_name: getTranslatedRoleName(
                          intl,
                          networkRoles?.admin.name,
                        ),
                      }}
                    />
                  </Dropdown.Item>
                )}
                {!isModerator && (
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="edit" />}
                    disabled={isUpdatingInvitation}
                    onClick={() =>
                      changeRole(memberInvitation, networkRoles.moderator)
                    }
                  >
                    <T
                      defaultMessage="Change role to {role_name}"
                      id="Admin.Members.ChangeRoleTo"
                      values={{
                        role_name: getTranslatedRoleName(
                          intl,
                          networkRoles?.moderator.name,
                        ),
                      }}
                    />
                  </Dropdown.Item>
                )}
                {isStaff && (
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="edit" />}
                    disabled={isUpdatingInvitation}
                    onClick={() =>
                      changeRole(memberInvitation, networkRoles.member)
                    }
                  >
                    <T
                      defaultMessage="Revoke {role_name} role"
                      id="Admin.Members.RevokeRole"
                      values={{
                        role_name: getTranslatedRoleName(intl, roleName),
                      }}
                    />
                  </Dropdown.Item>
                )}
              </Dropdown.Items>
            </Dropdown>
          )
        },
      },
    ],
    [
      $t,
      changeRole,
      intl,
      isUpdatingInvitation,
      onResendInvitation,
      onRevokeInvitation,
      roles,
      networkRoles,
    ],
  )

  return (
    <>
      <div className="w-full flex flex-col gap-3">
        <MassActionBanner massActionRequest={massActionRequest} />
        <Table
          storageId="invitations"
          isLoading={isInitialLoading}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          columns={columns}
          data={data}
          customTotalCount={totalInvitations}
          title={$t({
            defaultMessage: 'Invitations',
            id: 'Generics.Invitations',
          })}
          entityType="invitation"
          massActions={[
            MassActionRequestAction.Revoke,
            MassActionRequestAction.Resend,
          ]}
          onMassAction={setMassActionModal}
          massActionContext={MassActionRequestContext.MemberInvitation}
          keyboardControl
          actions={
            <Button
              size="lg"
              variant="primary"
              onClick={() => setInviteMembersModal(true)}
            >
              <T defaultMessage="Invite {MEMBERS}" id="Admin.Members.Invite" />
            </Button>
          }
          state={state}
          onStateChange={setState}
        />
      </div>
      {inviteMembersModal ? (
        <UserImportModal open onClose={() => setInviteMembersModal(false)} />
      ) : null}
      {!!massActionModal && (
        <MassActionModal
          open={!!massActionModal}
          onClose={() => setMassActionModal(null)}
          context={MassActionRequestContext.MemberInvitation}
          action={massActionModal}
          count={data?.pages?.[0]?.totalCount}
          onSuccess={setMassActionRequest}
          filterBy={newFilterBy}
        />
      )}
    </>
  )
}
